// Transport.jsx

import { useEffect, useMemo, useState } from "react";
import { studentDetailsTransportReport } from "../../../../api/StudentDetails";
import { SweetAlertShow } from "../../../master/Master";
import { Link, useNavigate } from "react-router-dom";
import { fmtMoney, Pill } from "../components/Master";
import TransportFeeDetailModal from "../components/TransportFeeDetailModal";
import TransportStatusModal from "../components/TransportStatusModal";
import TransportTransferHistoryModal from "../components/TransportTransferHistoryModal";

const Skeleton = ({ w = "100%", h = "14px" }) => (
  <span
    className="placeholder"
    style={{
      width: w,
      height: h,
      display: "inline-block",
      borderRadius: "6px",
    }}
  ></span>
);

const SummaryCard = ({
  icon,
  color,
  label,
  value,
  strikeValue,
  badgeValue,
  loading,
}) => (
  <div className="col-lg-4 col-md-6">
    <div
      className="p-3 bg-white rounded-3 h-100 d-flex align-items-center gap-3"
      style={{ border: "1px solid #eef0f4" }}
    >
      <div
        className={`d-flex align-items-center justify-content-center rounded-3 bg-${color} bg-opacity-10 flex-shrink-0`}
        style={{ width: 56, height: 56 }}
      >
        <i
          className={`${icon} text-${color === "primary" ? "white" : color}`}
          style={{ fontSize: 24 }}
        ></i>
      </div>

      <div>
        <small className="text-secondary">{label}</small>

        {loading ? (
          <div className="placeholder-glow mt-1">
            <Skeleton w="100px" h="24px" />
          </div>
        ) : (
          <>
            {strikeValue && (
              <div>
                <small className="text-decoration-line-through text-muted">
                  {strikeValue}
                </small>
              </div>
            )}
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h4 className={`mb-0 fw-bold text-${color}`}>{value}</h4>
              {badgeValue && (
                <span
                  className="text-warning fw-semibold"
                  style={{ fontSize: 14 }}
                >
                  ({badgeValue} Discount)
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : "-";

const MONTH_NAMES = [
  "",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const monthName = (m) => MONTH_NAMES[Number(m)] || m;

const transportStatusMeta = (status) => {
  switch (String(status)) {
    case "1":
      return { label: "Active", color: "success", icon: "ri-checkbox-circle-line" };
    case "2":
      return { label: "Inactive", color: "secondary", icon: "ri-close-circle-line" };
    case "5":
      return { label: "Completed", color: "primary", icon: "ri-flag-2-line" };
    default:
      return { label: "Unknown", color: "secondary", icon: "ri-question-line" };
  }
};

const transportFeeStatusMeta = (paid, final) => {
  const p = Number(paid || 0);
  const f = Number(final || 0);
  if (f > 0 && p >= f)
    return { label: "Paid", color: "success", icon: "ri-checkbox-circle-line" };
  if (p > 0) return { label: "Partial", color: "warning", icon: "ri-time-line" };
  return { label: "Due", color: "danger", icon: "ri-error-warning-line" };
};

export default function Transport({ id }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const [selectedEntry, setSelectedEntry] = useState(null);
  const [statusEntry, setStatusEntry] = useState(null);
  const [transferEntry, setTransferEntry] = useState(null);
  const navigate = useNavigate();


  const goToBusTransfer = (entry) => {
    const qs = new URLSearchParams({
      student_id: id ?? "",
      transport_n_fee_id: entry?.id ?? "",
    }).toString();

    navigate(`/student/bus/transfer?${qs}`);
  };

  const getStudentDetailsMain = async (student_id) => {
    setLoading(true);
    try {
      const response = await studentDetailsTransportReport(student_id);
      if (response.data.status) {
        setData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudentDetailsMain(id);
  }, [id]);

  const entries = data?.student_transport_n_fees || [];

  const sortedEntries = [...entries].sort(
    (a, b) =>
      new Date(b.joining_date || 0).getTime() -
      new Date(a.joining_date || 0).getTime(),
  );

  const overallTotals = useMemo(() => {
    return entries.reduce(
      (acc, e) => ({
        totalFee: acc.totalFee + Number(e.total_fee || 0),
        discount: acc.discount + Number(e.total_discount || 0),
        finalFee: acc.finalFee + Number(e.total_final_fee || 0),
        paid: acc.paid + Number(e.total_paid || 0),
        due: acc.due + Number(e.total_due || 0),
      }),
      { totalFee: 0, discount: 0, finalFee: 0, paid: 0, due: 0 },
    );
  }, [entries]);

  if (loading) {
    return (
      <div className="d-flex flex-column gap-3">
        <div className="row g-3">
          {[1, 2, 3].map((n) => (
            <div className="col-lg-4 col-md-6" key={n}>
              <div
                className="p-3 bg-white rounded-3 d-flex align-items-center gap-3"
                style={{ border: "1px solid #eef0f4" }}
              >
                <Skeleton w="56px" h="56px" />
                <div className="placeholder-glow d-flex flex-column gap-2 flex-grow-1">
                  <Skeleton h="14px" w="80px" />
                  <Skeleton h="24px" w="110px" />
                </div>
              </div>
            </div>
          ))}
        </div>
        {[1, 2].map((n) => (
          <div
            key={n}
            className="p-4 bg-white rounded-3"
            style={{ border: "1px solid #eef0f4" }}
          >
            <div className="placeholder-glow d-flex gap-2 mb-4">
              <Skeleton w="220px" h="28px" />
              <Skeleton w="160px" h="28px" />
              <Skeleton w="120px" h="28px" />
            </div>
            <div className="placeholder-glow mb-3">
              <Skeleton h="8px" />
            </div>
            <div className="row g-3">
              {[1, 2, 3, 4].map((c) => (
                <div className="col-3" key={c}>
                  <div className="placeholder-glow d-flex flex-column gap-2">
                    <Skeleton h="14px" />
                    <Skeleton h="20px" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!sortedEntries.length) {
    return (
      <div
        className="p-5 bg-white rounded-3 text-center text-muted"
        style={{ border: "1px solid #eef0f4" }}
      >
        <i className="ri-bus-line d-block fs-1 mb-2"></i>
        <p className="mb-0">No transport or fee details available</p>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {/* Overall Summary */}
      <div className="row g-3">
        <SummaryCard
          icon="ri-wallet-3-line"
          color="primary"
          label="Total Payable Fee"
          strikeValue={
            overallTotals.discount > 0 ? fmtMoney(overallTotals.totalFee) : null
          }
          value={fmtMoney(overallTotals.finalFee)}
          badgeValue={
            overallTotals.discount > 0 ? fmtMoney(overallTotals.discount) : null
          }
          loading={loading}
        />
        <SummaryCard
          icon="ri-checkbox-circle-line"
          color="success"
          label="Total Receive"
          value={fmtMoney(overallTotals.paid)}
          loading={loading}
        />
        <SummaryCard
          icon="ri-error-warning-line"
          color="danger"
          label="Total Due"
          value={fmtMoney(overallTotals.due)}
          loading={loading}
        />
      </div>

      {/* Per-entry cards */}
      {sortedEntries.map((entry) => {
        const total = Number(entry.total_fee || 0);
        const discount = Number(entry.total_discount || 0);
        const final = Number(entry.total_final_fee || 0);
        const paid = Number(entry.total_paid || 0);
        const pct = final ? Math.min(100, Math.round((paid / final) * 100)) : 0;

        const statusMeta = transportStatusMeta(entry.status);
        const feeDetails = entry?.bus_fee_details || [];
        const hasTransferHistory = Number(entry?.transfer_history_count || 0) > 0;

        return (
          <div
            key={entry.id}
            className="p-4 bg-white rounded-3"
            style={{ border: "1px solid #eef0f4" }}
          >
            {/* Header pills */}
            <div className="row justify-content-between align-items-start flex-wrap g-3 mb-3">
              <div className="col-md-8 d-flex flex-wrap gap-2">
                <Pill
                  icon="ri-bus-2-line"
                  color="primary"
                  label="Bus"
                  value={entry?.bus?.bus_number || "-"}
                />
                <Pill
                  icon="ri-map-pin-line"
                  color="success"
                  label="Stop"
                  value={entry?.stop?.stop_name || "-"}
                />
                <Pill
                  icon="ri-calendar-line"
                  color="info"
                  label="Payment"
                  value={entry?.payment_type || "-"}
                />
                <Pill
                  icon="ri-hashtag"
                  color="warning"
                  label="Id"
                  value={entry?.id}
                />

                <span
                  className={`badge rounded-pill cursor-pointer bg-${statusMeta.color} d-inline-flex align-items-center gap-1 px-3 py-2`}
                  onClick={() => setStatusEntry(entry)}
                >
                  <i className={statusMeta.icon}></i>
                  {statusMeta.label}
                </span>

                {hasTransferHistory && (
                  <span
                    className="badge rounded-pill cursor-pointer bg-label-info d-inline-flex align-items-center gap-1 px-3 py-2"
                    title="View Transfer History"
                    onClick={() => setTransferEntry(entry)}
                  >
                    <i className="ri-history-line"></i>
                    Transferred Histories ({entry.transfer_history_count})
                  </span>
                )}
              </div>

              <div className="col-md-4 text-end">

                <div className="d-flex align-items-center justify-content-end gap-2">
                  <Link
                    to={`/student/bus/enrollment/edit?id=${entry.id}&student_id=${id}`}
                    className="text-primary"
                    title="Edit Bus Enrollment"
                  >
                    <i className="ri-add-circle-line text-primary cursor-pointer fs-3"></i>
                  </Link>

                  <i
                    className="ri-shuffle-line text-primary cursor-pointer fs-3"
                    title="Transfer to Another Bus"
                    onClick={() => goToBusTransfer(entry)}
                  />


                  <small className="d-flex align-items-center gap-2 mb-0">
                    <i
                      className="ri-eye-line cursor-pointer fs-3 text-primary"
                      title="View Fee Details"
                      onClick={() => setSelectedEntry(entry)}
                    />
                    PAYABLE FEE
                  </small>
                </div>


                {discount > 0 && (
                  <small className="text-decoration-line-through text-muted d-block">
                    {fmtMoney(total)}
                  </small>
                )}
                <div className="d-flex align-items-center justify-content-end gap-2">
                  <h3 className="mb-0 fw-bold" style={{ color: "#6d5ee0" }}>
                    {fmtMoney(final)}
                  </h3>
                  {discount > 0 && (
                    <span className="badge bg-warning bg-opacity-10 text-warning">
                      {fmtMoney(discount)} discount
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="text-secondary">
                Received {fmtMoney(paid)} of {fmtMoney(final)}
              </small>
              <small
                className={`fw-semibold text-${pct >= 100 ? "success" : pct > 0 ? "warning" : "danger"}`}
              >
                {pct}%
              </small>
            </div>
            <div className="progress mb-3" style={{ height: 8 }}>
              <div
                className={`progress-bar bg-${pct >= 100 ? "success" : "warning"}`}
                style={{
                  width: `${pct}%`,
                  background: pct === 0 ? "#e5e0fb" : undefined,
                }}
              ></div>
            </div>

            {/* Route / Driver Info */}
            <div
              className="d-flex flex-wrap gap-4 pt-3"
              style={{
                borderBottom: feeDetails.length ? "1px solid #f0f0f0" : "none",
                paddingBottom: feeDetails.length ? "1rem" : 0,
              }}
            >
              <small className="text-secondary d-flex align-items-center gap-1">
                <i className="ri-user-star-line text-info"></i>
                Driver:
                <span className="fw-medium text-dark">
                  {entry?.bus?.driver_name || "-"}
                </span>
              </small>

              {entry?.bus?.driver_number && (
                <small className="text-secondary d-flex align-items-center gap-1">
                  <i className="ri-phone-line text-success"></i>
                  Mobile:

                  <a href={`tel:${entry.bus.driver_number}`}
                    className="fw-medium text-dark text-decoration-none"
                  >
                    {entry.bus.driver_number}
                  </a>
                </small>
              )}

              <small className="text-secondary d-flex align-items-center gap-1">
                <i className="ri-user-follow-line text-warning"></i>
                Capacity:
                <span className="fw-medium text-dark">
                  {entry?.bus?.capacity != null
                    ? `${entry.bus.capacity} Students`
                    : "-"}
                </span>
              </small>

              <small className="text-secondary d-flex align-items-center gap-1">
                <i className="ri-price-tag-3-line text-warning"></i>
                Discount Remark:
                <span className="fw-medium text-dark">
                  {entry?.discount_remark || "-"}
                </span>
              </small>

              <small className="text-secondary d-flex align-items-center gap-1">
                <i className="ri-calendar-check-line text-success"></i>
                Joined:
                <span className="fw-medium text-dark">
                  {fmtDate(entry?.joining_date)}
                </span>
              </small>

              {entry?.end_date && (
                <small className="text-secondary d-flex align-items-center gap-1">
                  <i className="ri-calendar-close-line text-danger"></i>
                  Ends:
                  <span className="fw-medium text-dark">
                    {fmtDate(entry?.end_date)}
                  </span>
                </small>
              )}

              {entry?.reason && (
                <small className="text-secondary d-flex align-items-center gap-1">
                  <i className="ri-chat-quote-line text-primary"></i>
                  Reason:
                  <span className="fw-medium text-dark">{entry.reason}</span>
                </small>
              )}
            </div>

            {/* Fee Breakdown Table (month-wise) */}
            {feeDetails.length > 0 && (
              <div className="pt-3">
                <small className="text-secondary fw-semibold d-flex align-items-center gap-1 mb-2">
                  <i className="ri-file-list-3-line"></i> FEE BREAKDOWN
                </small>
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>Period</th>
                        <th>Total</th>
                        <th>Discount</th>
                        <th>Final</th>
                        <th>Paid</th>
                        <th>Due</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feeDetails.map((f, i) => {
                        const meta = transportFeeStatusMeta(
                          f.paid_amount,
                          f.final_amount,
                        );
                        return (
                          <tr key={f.id}>
                            <td>{i + 1}{" "}#{f?.id} </td>
                            <td>
                              <span className="d-flex align-items-center gap-1">
                                <i className="ri-calendar-2-line text-secondary"></i>
                                {monthName(f.month)} {f.year}
                                <span className="text-muted ms-1">
                                  ({f.type})
                                </span>
                              </span>
                            </td>
                            <td>{fmtMoney(f.actual_amount)}</td>
                            <td className="text-warning">
                              {fmtMoney(f.discount)}
                            </td>
                            <td>{fmtMoney(f.final_amount)}</td>
                            <td className="text-success">
                              {fmtMoney(f.paid_amount)}
                            </td>
                            <td className="text-danger">
                              {fmtMoney(f.due_amount)}
                            </td>
                            <td>
                              <span
                                className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color} d-inline-flex align-items-center gap-1`}
                              >
                                <i className={meta.icon}></i>
                                {meta.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Fee Detail Modal */}
      {selectedEntry && (
        <TransportFeeDetailModal
          entry={selectedEntry}
          onClose={() => setSelectedEntry(null)}
          onUpdated={() => {
            setSelectedEntry(null);
            getStudentDetailsMain(id);
          }}
        />
      )}

      {/* Status Modal */}
      {statusEntry && (
        <TransportStatusModal
          entry={statusEntry}
          onClose={() => setStatusEntry(null)}
          onUpdated={() => {
            setStatusEntry(null);
            getStudentDetailsMain(id);
          }}
        />
      )}

      {/* Transfer History Modal */}
      {transferEntry && (
        <TransportTransferHistoryModal
          entry={transferEntry}
          onClose={() => setTransferEntry(null)}
        />
      )}
    </div>
  );
}